import React, { useState, useRef, useEffect } from 'react';
import { X, RotateCw, RotateCcw, ZoomIn, ZoomOut, Check, Crop, RefreshCw } from 'lucide-react';
import { useLang } from '../../core/lang.jsx';
import LangKeys from '../../core/locales/keys';

const ImageCropperModal = ({
  isOpen,
  imageSrc,
  onClose,
  onCropComplete,
  aspectRatio = 1, // 1 for 1:1 square, or 'free', 4/3
  title
}) => {
  const { t } = useLang();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const [imageObj, setImageObj] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0); // 0, 90, 180, 270
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectedRatio, setSelectedRatio] = useState(aspectRatio); // 1, 4/3, 'free'
  const [saving, setSaving] = useState(false);

  // Load image when imageSrc changes
  useEffect(() => {
    if (!imageSrc || !isOpen) {
      setImageObj(null);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImageObj(img);
      setZoom(1);
      setRotation(0);
      setOffset({ x: 0, y: 0 });
    };
    img.src = imageSrc;
  }, [imageSrc, isOpen]);

  // Redraw canvas whenever zoom, rotation, offset or image changes
  useEffect(() => {
    if (!imageObj || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear
    ctx.clearRect(0, 0, width, height);
    ctx.save();

    // Background dark filler
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    // Center transform
    ctx.translate(width / 2 + offset.x, height / 2 + offset.y);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(zoom, zoom);

    // Calculate image base scale to fit canvas
    const imgRatio = imageObj.width / imageObj.height;
    let drawW, drawH;

    if (imgRatio > 1) {
      drawW = width * 0.85;
      drawH = drawW / imgRatio;
    } else {
      drawH = height * 0.85;
      drawW = drawH * imgRatio;
    }

    ctx.drawImage(imageObj, -drawW / 2, -drawH / 2, drawW, drawH);
    ctx.restore();

    // Draw Crop Frame Overlay
    drawOverlay(ctx, width, height);
  }, [imageObj, zoom, rotation, offset, selectedRatio]);

  const getCropBoxRect = (w, h) => {
    const size = Math.min(w, h) * 0.82;
    if (selectedRatio === 'free') {
      return { x: (w - size) / 2, y: (h - size * 0.75) / 2, width: size, height: size * 0.75 };
    }
    if (selectedRatio === 4 / 3) {
      const cropW = size;
      const cropH = size * (3 / 4);
      return { x: (w - cropW) / 2, y: (h - cropH) / 2, width: cropW, height: cropH };
    }
    // 1:1 Square
    return { x: (w - size) / 2, y: (h - size) / 2, width: size, height: size };
  };

  const drawOverlay = (ctx, w, h) => {
    const crop = getCropBoxRect(w, h);

    // Dark semi-transparent overlay outside crop box
    ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
    ctx.beginPath();
    ctx.rect(0, 0, w, h);
    ctx.rect(crop.x + crop.width, crop.y, -crop.width, crop.height); // cutout
    ctx.fill();

    // Crop border
    ctx.strokeStyle = '#38BDF8';
    ctx.lineWidth = 2;
    ctx.strokeRect(crop.x, crop.y, crop.width, crop.height);

    // Rule of thirds grid lines
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);

    // Vertical grid lines
    ctx.beginPath();
    ctx.moveTo(crop.x + crop.width / 3, crop.y);
    ctx.lineTo(crop.x + crop.width / 3, crop.y + crop.height);
    ctx.moveTo(crop.x + (crop.width * 2) / 3, crop.y);
    ctx.lineTo(crop.x + (crop.width * 2) / 3, crop.y + crop.height);

    // Horizontal grid lines
    ctx.moveTo(crop.x, crop.y + crop.height / 3);
    ctx.lineTo(crop.x + crop.width, crop.y + crop.height / 3);
    ctx.moveTo(crop.x, crop.y + (crop.height * 2) / 3);
    ctx.lineTo(crop.x + crop.width, crop.y + (crop.height * 2) / 3);
    ctx.stroke();
    ctx.setLineDash([]); // reset

    // Corner guides
    const cornerLen = 16;
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 3;
    // Top-Left
    ctx.beginPath();
    ctx.moveTo(crop.x, crop.y + cornerLen);
    ctx.lineTo(crop.x, crop.y);
    ctx.lineTo(crop.x + cornerLen, crop.y);
    // Top-Right
    ctx.moveTo(crop.x + crop.width - cornerLen, crop.y);
    ctx.lineTo(crop.x + crop.width, crop.y);
    ctx.lineTo(crop.x + crop.width, crop.y + cornerLen);
    // Bottom-Left
    ctx.moveTo(crop.x, crop.y + crop.height - cornerLen);
    ctx.lineTo(crop.x, crop.y + crop.height);
    ctx.lineTo(crop.x + cornerLen, crop.y + crop.height);
    // Bottom-Right
    ctx.moveTo(crop.x + crop.width - cornerLen, crop.y + crop.height);
    ctx.lineTo(crop.x + crop.width, crop.y + crop.height);
    ctx.lineTo(crop.x + crop.width, crop.y + crop.height - cornerLen);
    ctx.stroke();
  };

  // Mouse / Touch handlers for dragging
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setOffset({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => setIsDragging(false);

  // Touch handlers
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - offset.x,
        y: e.touches[0].clientY - offset.y
      });
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging || e.touches.length !== 1) return;
    setOffset({
      x: e.touches[0].clientX - dragStart.x,
      y: e.touches[0].clientY - dragStart.y
    });
  };

  const handleTouchEnd = () => setIsDragging(false);

  // Wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY * -0.0015;
    setZoom((prev) => Math.min(Math.max(0.6, prev + delta), 3.5));
  };

  const handleRotate = (deg) => {
    setRotation((prev) => (prev + deg + 360) % 360);
  };

  const handleReset = () => {
    setZoom(1);
    setRotation(0);
    setOffset({ x: 0, y: 0 });
  };

  // Execute Crop & Compress to HD web format (<200KB)
  const handleCropAndSave = async () => {
    if (!imageObj || !canvasRef.current) return;
    setSaving(true);

    try {
      const sourceCanvas = canvasRef.current;
      const crop = getCropBoxRect(sourceCanvas.width, sourceCanvas.height);

      // Create high-res destination canvas
      const outputCanvas = document.createElement('canvas');
      const TARGET_DIM = 1024; // 1024x1024 max HD dimension for fast cloud sync

      let outW = TARGET_DIM;
      let outH = TARGET_DIM;
      if (selectedRatio === 4 / 3) {
        outH = Math.round(TARGET_DIM * (3 / 4));
      } else if (selectedRatio === 'free') {
        outH = Math.round(TARGET_DIM * (crop.height / crop.width));
      }

      outputCanvas.width = outW;
      outputCanvas.height = outH;
      const outCtx = outputCanvas.getContext('2d', { willReadFrequently: true });

      // High quality smoothing
      outCtx.imageSmoothingEnabled = true;
      outCtx.imageSmoothingQuality = 'high';

      // White background behind image
      outCtx.fillStyle = '#FFFFFF';
      outCtx.fillRect(0, 0, outW, outH);

      // Recreate image transform on high-res canvas
      outCtx.save();
      outCtx.translate(outW / 2, outH / 2);
      outCtx.rotate((rotation * Math.PI) / 180);
      outCtx.scale(zoom, zoom);

      // Scale factor from preview canvas to high-res output
      const scaleFactor = outW / crop.width;
      outCtx.translate(
        (offset.x + (sourceCanvas.width / 2 - (crop.x + crop.width / 2))) * scaleFactor,
        (offset.y + (sourceCanvas.height / 2 - (crop.y + crop.height / 2))) * scaleFactor
      );

      const imgRatio = imageObj.width / imageObj.height;
      let drawW, drawH;
      if (imgRatio > 1) {
        drawW = sourceCanvas.width * 0.85 * scaleFactor;
        drawH = drawW / imgRatio;
      } else {
        drawH = sourceCanvas.height * 0.85 * scaleFactor;
        drawW = drawH * imgRatio;
      }

      outCtx.drawImage(imageObj, -drawW / 2, -drawH / 2, drawW, drawH);
      outCtx.restore();

      // Convert to compressed JPEG Blob (75% quality = crisp HD under 150KB)
      outputCanvas.toBlob(
        (blob) => {
          if (blob) {
            const fileName = `item_photo_${Date.now()}.jpg`;
            const croppedFile = new File([blob], fileName, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            const previewUrl = URL.createObjectURL(blob);
            onCropComplete(croppedFile, previewUrl);
            onClose();
          }
          setSaving(false);
        },
        'image/jpeg',
        0.75
      );
    } catch (err) {
      console.error('Cropping error:', err);
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Crop size={20} color="#3B82F6" />
            <h3 style={styles.title}>{title || t(LangKeys.crop_photo_title) || 'Crop & Adjust Photo'}</h3>
          </div>
          <button type="button" style={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Canvas Workspace */}
        <div
          ref={containerRef}
          style={styles.canvasContainer}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheel}
        >
          <canvas ref={canvasRef} width={380} height={380} style={styles.canvas} />
          <div style={styles.dragHint}>{t(LangKeys.crop_drag_hint) || 'Drag image to reposition'}</div>
        </div>

        {/* Controls Toolbar */}
        <div style={styles.toolbar}>
          {/* Zoom Slider */}
          <div style={styles.controlRow}>
            <ZoomOut size={16} color="#64748B" />
            <input
              type="range"
              min="0.7"
              max="3"
              step="0.05"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              style={styles.slider}
            />
            <ZoomIn size={16} color="#64748B" />
            <span style={styles.zoomLabel}>{Math.round(zoom * 100)}%</span>
          </div>

          {/* Action Tools */}
          <div style={styles.toolButtonsRow}>
            {/* Rotate 90 deg */}
            <button
              type="button"
              style={styles.toolBtn}
              onClick={() => handleRotate(-90)}
              title="Rotate Left"
            >
              <RotateCcw size={15} /> {t(LangKeys.crop_rotate_left) || '90° Left'}
            </button>
            <button
              type="button"
              style={styles.toolBtn}
              onClick={() => handleRotate(90)}
              title="Rotate Right"
            >
              <RotateCw size={15} /> {t(LangKeys.crop_rotate_right) || '90° Right'}
            </button>

            {/* Reset */}
            <button
              type="button"
              style={styles.toolBtn}
              onClick={handleReset}
              title="Reset"
            >
              <RefreshCw size={14} /> {t(LangKeys.crop_reset) || 'Reset'}
            </button>
          </div>

          {/* Aspect Ratio Buttons */}
          <div style={styles.ratioRow}>
            <button
              type="button"
              style={{
                ...styles.ratioBtn,
                ...(selectedRatio === 1 ? styles.ratioBtnActive : {})
              }}
              onClick={() => setSelectedRatio(1)}
            >
              {t(LangKeys.crop_ratio_square) || '1:1 (Square)'}
            </button>
            <button
              type="button"
              style={{
                ...styles.ratioBtn,
                ...(selectedRatio === 4 / 3 ? styles.ratioBtnActive : {})
              }}
              onClick={() => setSelectedRatio(4 / 3)}
            >
              {t(LangKeys.crop_ratio_standard) || '4:3 (Standard)'}
            </button>
            <button
              type="button"
              style={{
                ...styles.ratioBtn,
                ...(selectedRatio === 'free' ? styles.ratioBtnActive : {})
              }}
              onClick={() => setSelectedRatio('free')}
            >
              {t(LangKeys.crop_ratio_free) || 'Free'}
            </button>
          </div>
        </div>

        {/* Footer Actions */}
        <div style={styles.footer}>
          <button type="button" style={styles.cancelBtn} onClick={onClose} disabled={saving}>
            {t(LangKeys.cancel) || 'Cancel'}
          </button>
          <button
            type="button"
            style={styles.saveBtn}
            onClick={handleCropAndSave}
            disabled={saving || !imageObj}
          >
            {saving ? (
              <span>{t(LangKeys.crop_compressing) || 'Compressing...'}</span>
            ) : (
              <>
                <Check size={18} strokeWidth={2.5} /> {t(LangKeys.crop_save_done) || 'Crop & Save (Done)'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.75)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99999,
    padding: '16px'
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '430px',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    display: 'flex',
    flexDirection: 'column',
    animation: 'fadeIn 0.2s ease-out'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    borderBottom: '1px solid #E2E8F0',
    backgroundColor: '#F8FAFC'
  },
  title: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#0F172A',
    margin: 0
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#64748B',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    borderRadius: '6px'
  },
  canvasContainer: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#0F172A',
    userSelect: 'none',
    touchAction: 'none',
    cursor: 'grab',
    overflow: 'hidden'
  },
  canvas: {
    display: 'block',
    maxWidth: '100%',
    height: 'auto'
  },
  dragHint: {
    position: 'absolute',
    bottom: '10px',
    backgroundColor: 'rgba(0,0,0,0.6)',
    color: '#F8FAFC',
    fontSize: '11px',
    padding: '4px 10px',
    borderRadius: '20px',
    pointerEvents: 'none'
  },
  toolbar: {
    padding: '14px 16px',
    backgroundColor: '#F8FAFC',
    borderTop: '1px solid #E2E8F0',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  controlRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  slider: {
    flex: 1,
    accentColor: '#2563EB',
    cursor: 'pointer'
  },
  zoomLabel: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#475569',
    minWidth: '40px',
    textAlign: 'right'
  },
  toolButtonsRow: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center'
  },
  toolBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '6px 12px',
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #CBD5E1',
    color: '#334155',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  ratioRow: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center'
  },
  ratioBtn: {
    flex: 1,
    padding: '6px 8px',
    borderRadius: '8px',
    border: '1px solid #CBD5E1',
    backgroundColor: '#FFFFFF',
    color: '#64748B',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    textAlign: 'center'
  },
  ratioBtnActive: {
    backgroundColor: '#EFF6FF',
    borderColor: '#3B82F6',
    color: '#2563EB'
  },
  footer: {
    display: 'flex',
    gap: '10px',
    padding: '14px 16px',
    borderTop: '1px solid #E2E8F0',
    backgroundColor: '#FFFFFF'
  },
  cancelBtn: {
    flex: 1,
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1px solid #CBD5E1',
    backgroundColor: '#FFFFFF',
    color: '#475569',
    fontSize: '13.5px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  saveBtn: {
    flex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '10px 14px',
    borderRadius: '10px',
    border: 'none',
    backgroundColor: '#2563EB',
    color: '#FFFFFF',
    fontSize: '13.5px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.25)'
  }
};

export default ImageCropperModal;
